/**
 * Zero-Setup Kasse - KI-gestützte automatische Einrichtung
 * 
 * Ziel: Nutzer kann in < 10 Minuten verkaufen - OHNE manuelle Konfiguration
 * 
 * Flow:
 * 1. Betriebsart wählen (Restaurant, Imbiss, Café, Lieferdienst)
 * 2. Speisekarte hochladen (Foto/PDF)
 * 3. Klick auf "Kasse einrichten"
 * 4. KI macht ALLES automatisch!
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { 
  Utensils, Coffee, Truck, Store,
  Upload, Sparkles, Check, Loader2, 
  Camera, FileText, ArrowRight, Rocket,
  Clock, Zap, PartyPopper
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Betriebsarten
const businessTypes = [
  {
    id: 'restaurant',
    name: 'Restaurant',
    icon: Utensils,
    description: 'Klassisches Restaurant mit Tischservice',
    color: 'from-orange-500 to-red-500',
    features: ['Tischverwaltung', 'Splitrechnung', 'Trinkgeld']
  },
  {
    id: 'imbiss',
    name: 'Imbiss',
    icon: Store,
    description: 'Schnellrestaurant mit Thekenverkauf',
    color: 'from-yellow-500 to-orange-500',
    features: ['Schnellbestellung', 'Abholung', 'To-Go']
  },
  {
    id: 'cafe',
    name: 'Café',
    icon: Coffee,
    description: 'Café mit Kaffee & Kuchen',
    color: 'from-amber-600 to-yellow-500',
    features: ['Tischservice', 'Stempelkarte', 'Frühstück']
  },
  {
    id: 'lieferdienst',
    name: 'Lieferdienst',
    icon: Truck,
    description: 'Pizza, Döner & mehr mit Lieferung',
    color: 'from-blue-500 to-cyan-500',
    features: ['Lieferverfolgung', 'Fahrerzuweisung', 'Online']
  }
];

export default function ZeroSetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [menuFile, setMenuFile] = useState(null);
  const [menuPreview, setMenuPreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  
  const sessionToken = localStorage.getItem('access_token') || localStorage.getItem('access_token');

  // Check if user is logged in
  React.useEffect(() => {
    if (!sessionToken) {
      toast.error('Bitte melden Sie sich zuerst an');
      navigate('/login?redirect=/zero-setup');
    }
  }, [sessionToken, navigate]);

  // Datei-Upload Handler
  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validierung
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Bitte ein Bild (JPG, PNG) oder PDF hochladen');
      return;
    }
    
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Datei zu groß (max 15MB)');
      return;
    }
    
    setMenuFile(file);
    
    // Preview erstellen
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setMenuPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setMenuPreview(null);
    }
    
    toast.success('Speisekarte hochgeladen!');
  }, []);

  // Drag & Drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } };
      handleFileChange(fakeEvent);
    }
  }, [handleFileChange]);

  // Zero-Setup starten
  const startSetup = async () => {
    if (!selectedType || !menuFile) {
      toast.error('Bitte wählen Sie eine Betriebsart und laden Sie eine Speisekarte hoch');
      return;
    }
    
    setIsProcessing(true);
    setProgress(10);
    
    try {
      // FormData erstellen
      const formData = new FormData();
      formData.append('business_type', selectedType);
      formData.append('menu_file', menuFile);
      if (businessName) {
        formData.append('business_name', businessName);
      }
      
      setProgress(30);
      toast.info('🤖 KI analysiert Ihre Speisekarte...');
      
      const response = await fetch(`${BACKEND_URL}/api/zero-setup/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        },
        body: formData
      });
      
      setProgress(70);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Setup fehlgeschlagen');
      }
      
      const data = await response.json();
      setProgress(100);
      setResult(data);
      setStep(4);
      
      toast.success('🎉 Ihre Kasse ist bereit!');
      
    } catch (error) {
      console.error('Setup error:', error);
      toast.error(error.message || 'Setup fehlgeschlagen');
    } finally {
      setIsProcessing(false);
    }
  };

  // Demo-Setup (ohne Bild)
  const startDemoSetup = async () => {
    if (!selectedType) {
      toast.error('Bitte wählen Sie eine Betriebsart');
      return;
    }
    
    setIsProcessing(true);
    setProgress(30);
    
    try {
      toast.info('🚀 Demo wird eingerichtet...');
      
      const response = await fetch(`${BACKEND_URL}/api/zero-setup/quick-demo?business_type=${selectedType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });
      
      setProgress(80);
      
      if (!response.ok) {
        throw new Error('Demo-Setup fehlgeschlagen');
      }
      
      const data = await response.json();
      setProgress(100);
      setResult(data);
      setStep(4);
      
      toast.success('🎉 Demo-Kasse ist bereit!');
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="border-b border-white/10 bg-black/20 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Zero-Setup Kasse</h1>
                <p className="text-sm text-slate-400">In 10 Minuten verkaufsbereit</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-slate-400">
              <Clock className="w-4 h-4" />
              <span className="text-sm">~10 Min</span>
            </div>
          </div>
          
          {/* Progress */}
          <div className="mt-6 flex items-center gap-4">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex-1 flex items-center gap-2">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all
                  ${step >= s 
                    ? 'bg-gradient-to-br from-emerald-500 to-cyan-500 text-white' 
                    : 'bg-slate-700 text-slate-400'
                  }
                `}>
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 4 && (
                  <div className={`flex-1 h-1 rounded ${step > s ? 'bg-emerald-500' : 'bg-slate-700'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        <AnimatePresence mode="wait">
          
          {/* Step 1: Betriebsart wählen */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <h2 className="text-3xl font-bold text-white mb-3">
                  Was für ein Betrieb sind Sie?
                </h2>
                <p className="text-slate-400">
                  Wir passen Ihre Kasse automatisch an Ihre Bedürfnisse an
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {businessTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = selectedType === type.id;
                  
                  return (
                    <Card
                      key={type.id}
                      onClick={() => setSelectedType(type.id)}
                      className={`
                        p-6 cursor-pointer transition-all duration-300 border-2
                        ${isSelected 
                          ? 'border-emerald-500 bg-emerald-500/10' 
                          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                        }
                      `}
                      data-testid={`business-type-${type.id}`}
                    >
                      <div className={`
                        w-14 h-14 rounded-2xl flex items-center justify-center mb-4
                        bg-gradient-to-br ${type.color}
                      `}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      
                      <h3 className="text-lg font-semibold text-white mb-1">
                        {type.name}
                      </h3>
                      <p className="text-sm text-slate-400 mb-3">
                        {type.description}
                      </p>
                      
                      <div className="flex flex-wrap gap-1">
                        {type.features.map((f) => (
                          <span key={f} className="text-xs px-2 py-0.5 bg-slate-700 text-slate-300 rounded">
                            {f}
                          </span>
                        ))}
                      </div>
                      
                      {isSelected && (
                        <div className="mt-4 flex items-center gap-2 text-emerald-400">
                          <Check className="w-4 h-4" />
                          <span className="text-sm font-medium">Ausgewählt</span>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
              
              <div className="flex justify-end">
                <Button
                  onClick={() => setStep(2)}
                  disabled={!selectedType}
                  className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600"
                  data-testid="next-step-1"
                >
                  Weiter
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Speisekarte hochladen */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <h2 className="text-3xl font-bold text-white mb-3">
                  Laden Sie Ihre Speisekarte hoch
                </h2>
                <p className="text-slate-400">
                  Ein Foto oder PDF genügt - unsere KI macht den Rest
                </p>
              </div>

              {/* Optional: Business Name */}
              <div className="max-w-md mx-auto">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Name Ihres Betriebs (optional)
                </label>
                <Input
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="z.B. Pizzeria Milano"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="business-name-input"
                />
              </div>

              {/* Upload Area */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className={`
                  max-w-2xl mx-auto border-2 border-dashed rounded-2xl p-10 text-center
                  transition-all duration-300 cursor-pointer
                  ${menuFile 
                    ? 'border-emerald-500 bg-emerald-500/10' 
                    : 'border-slate-600 bg-slate-800/50 hover:border-slate-500'
                  }
                `}
              >
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="menu-upload"
                  data-testid="menu-file-input"
                />
                <label htmlFor="menu-upload" className="cursor-pointer">
                  {menuFile ? (
                    <div className="space-y-4">
                      {menuPreview ? (
                        <img 
                          src={menuPreview} 
                          alt="Speisekarte" 
                          className="max-h-64 mx-auto rounded-lg shadow-lg"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto">
                          <FileText className="w-10 h-10 text-emerald-400" />
                        </div>
                      )}
                      <div>
                        <p className="text-white font-medium">{menuFile.name}</p>
                        <p className="text-sm text-slate-400">
                          {(menuFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <Button variant="outline" size="sm" className="border-slate-600">
                        Andere Datei wählen
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="w-20 h-20 bg-slate-700 rounded-2xl flex items-center justify-center mx-auto">
                        <Upload className="w-10 h-10 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          Speisekarte hier ablegen
                        </p>
                        <p className="text-sm text-slate-400">
                          oder klicken zum Auswählen
                        </p>
                      </div>
                      <div className="flex items-center justify-center gap-4 text-slate-500">
                        <div className="flex items-center gap-1">
                          <Camera className="w-4 h-4" />
                          <span className="text-xs">Foto</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          <span className="text-xs">PDF</span>
                        </div>
                      </div>
                    </div>
                  )}
                </label>
              </div>

              {/* KI Info */}
              <div className="max-w-2xl mx-auto bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white mb-1">KI-Power aktiviert</h4>
                    <p className="text-sm text-slate-400">
                      Unsere KI erkennt automatisch alle Produkte, Preise und Kategorien. 
                      Außerdem werden MwSt-Sätze und Drucker-Rollen korrekt zugewiesen.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="border-slate-600"
                >
                  Zurück
                </Button>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={startDemoSetup}
                    className="border-slate-600"
                    data-testid="demo-setup-btn"
                  >
                    Demo ohne Speisekarte
                  </Button>
                  
                  <Button
                    onClick={() => setStep(3)}
                    disabled={!menuFile}
                    className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600"
                    data-testid="next-step-2"
                  >
                    Weiter
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Bestätigung & Start */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <h2 className="text-3xl font-bold text-white mb-3">
                  Alles bereit!
                </h2>
                <p className="text-slate-400">
                  Ein Klick und Ihre Kasse ist einsatzbereit
                </p>
              </div>

              {/* Zusammenfassung */}
              <Card className="max-w-2xl mx-auto bg-slate-800/50 border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Zusammenfassung</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Betriebsart</span>
                    <span className="text-white font-medium">
                      {businessTypes.find(t => t.id === selectedType)?.name}
                    </span>
                  </div>
                  
                  {businessName && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Name</span>
                      <span className="text-white font-medium">{businessName}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Speisekarte</span>
                    <span className="text-white font-medium">{menuFile?.name}</span>
                  </div>
                </div>
                
                <hr className="my-4 border-slate-700" />
                
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-white">Die KI wird automatisch:</h4>
                  <ul className="text-sm text-slate-400 space-y-1">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-400" />
                      Alle Artikel & Preise erkennen
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-400" />
                      Kategorien erstellen
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-400" />
                      MwSt-Sätze korrekt zuordnen (7% / 19%)
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-400" />
                      Drucker-Rollen einrichten
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-400" />
                      Tische anlegen (falls Restaurant/Café)
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-400" />
                      QR-Bestellung aktivieren
                    </li>
                  </ul>
                </div>
              </Card>

              {/* Progress während Verarbeitung */}
              {isProcessing && (
                <div className="max-w-2xl mx-auto space-y-4">
                  <Progress value={progress} className="h-2" />
                  <div className="flex items-center justify-center gap-2 text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>
                      {progress < 30 && 'Speisekarte wird analysiert...'}
                      {progress >= 30 && progress < 70 && '🤖 KI extrahiert Produkte...'}
                      {progress >= 70 && progress < 100 && '⚙️ System wird eingerichtet...'}
                      {progress === 100 && '✅ Fertig!'}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  disabled={isProcessing}
                  className="border-slate-600"
                >
                  Zurück
                </Button>
                
                <Button
                  onClick={startSetup}
                  disabled={isProcessing}
                  size="lg"
                  className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 px-8"
                  data-testid="start-setup-btn"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Wird eingerichtet...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-5 h-5 mr-2" />
                      Kasse einrichten
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Erfolg! */}
          {step === 4 && result && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-8"
            >
              <div className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto">
                <PartyPopper className="w-12 h-12 text-white" />
              </div>
              
              <div>
                <h2 className="text-4xl font-bold text-white mb-3">
                  🎉 Ihre Kasse ist bereit!
                </h2>
                <p className="text-xl text-slate-400">
                  {result.message}
                </p>
              </div>

              {/* Statistiken */}
              <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
                <Card className="bg-slate-800/50 border-slate-700 p-4">
                  <div className="text-3xl font-bold text-emerald-400">{result.products_created}</div>
                  <div className="text-sm text-slate-400">Produkte</div>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700 p-4">
                  <div className="text-3xl font-bold text-cyan-400">{result.categories_created}</div>
                  <div className="text-sm text-slate-400">Kategorien</div>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700 p-4">
                  <div className="text-3xl font-bold text-purple-400">
                    {result.settings_configured?.length || 0}
                  </div>
                  <div className="text-sm text-slate-400">Einstellungen</div>
                </Card>
              </div>

              {/* Was wurde eingerichtet */}
              <Card className="max-w-lg mx-auto bg-slate-800/50 border-slate-700 p-6 text-left">
                <h3 className="font-medium text-white mb-3">Automatisch eingerichtet:</h3>
                <ul className="space-y-2">
                  {result.settings_configured?.map((setting, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-300">
                      <Check className="w-4 h-4 text-emerald-400" />
                      {setting}
                    </li>
                  ))}
                </ul>
              </Card>

              <div className="flex justify-center gap-4">
                <Button
                  onClick={() => navigate('/app/kassier')}
                  size="lg"
                  className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 px-8"
                  data-testid="go-to-pos-btn"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Zur Kasse
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => navigate('/app/produkte')}
                  className="border-slate-600"
                >
                  Produkte ansehen
                </Button>
              </div>
            </motion.div>
          )}
          
        </AnimatePresence>
      </div>
    </div>
  );
}
